import { memo, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, LabelList } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartConfig,
} from '@parrot-software/design-system';
import type { TransformedChartData } from '@reports/presentation/utils/chartDataTransformers';
import { formatAxisValue } from '@reports/presentation/utils/dataFormatters';
import { ReportDataFormats } from '@reports/domain/types';
import type { ReportDataConfig } from '@reports/domain/types';

export interface ReportBarChartProps {
  chartData: TransformedChartData[];
  barColor: string;
  showTooltip: boolean;
  showTopBarLabels: boolean;
  showLegend: boolean;
  tooltipFormatter: (value: any, name: string | number, item: any) => React.ReactElement;
  topLabelFormatter: (value: number) => string;
  chartConfig: ChartConfig;
  format?: ReportDataConfig;
}

/**
 * ReportBarChart - Bar chart renderer for reports
 *
 * Renders a bar chart using Recharts with optional tooltips and top labels.
 * Automatically adds currency symbol ($) to YAxis when format type is CURRENCY.
 * Memoized to prevent unnecessary re-renders.
 *
 * @param {ReportBarChartProps} props - Component properties
 * @param {TransformedChartData[]} props.chartData - Chart data in Recharts format
 * @param {string} props.barColor - Bar fill color
 * @param {boolean} props.showTooltip - Whether to show tooltips on hover
 * @param {boolean} props.showTopBarLabels - Whether to show labels on top of bars
 * @param {boolean} props.showLegend - Whether to show chart legend
 * @param {Function} props.tooltipFormatter - Function to format tooltip content
 * @param {Function} props.topLabelFormatter - Function to format top labels
 * @param {ChartConfig} props.chartConfig - Chart configuration
 * @param {ReportDataConfig} [props.format] - Data format configuration for axis formatting
 * @returns {React.ReactElement} Bar chart component
 *
 * @example
 * <ReportBarChart
 *   chartData={transformedData}
 *   barColor="#EB6668"
 *   showTooltip={true}
 *   showTopBarLabels={true}
 *   showLegend={true}
 *   tooltipFormatter={barTooltipFormatter}
 *   topLabelFormatter={topLabelFormatter}
 *   chartConfig={{ value: { label: "Total Sales MXN", color: "#000000" } }}
 *   format={{ type: ReportDataFormats.CURRENCY }}
 * />
 */
const ReportBarChartComponent = ({
  chartData,
  barColor,
  showTooltip,
  showTopBarLabels,
  showLegend,
  tooltipFormatter,
  topLabelFormatter,
  chartConfig,
  format,
}: ReportBarChartProps) => {
  // Create YAxis formatter that adds "$" prefix for currency format
  const yAxisFormatter = useMemo(() => {
    const isCurrency = format?.type === ReportDataFormats.CURRENCY;

    return (value: number): string => {
      const formattedValue = formatAxisValue(value);
      return isCurrency && formattedValue ? `$${formattedValue}` : formattedValue;
    };
  }, [format]);

  return (
    <ChartContainer className="h-[275px] w-full" config={chartConfig}>
      <ResponsiveContainer height="100%" width="100%">
        <BarChart data={chartData} margin={{ top: 20, left: 4, right: 4 }}>
          <CartesianGrid vertical={false} />
          <XAxis axisLine={false} dataKey="name" interval="preserveStartEnd" minTickGap={16} tickLine={false} />
          <YAxis axisLine={false} tickFormatter={yAxisFormatter} tickLine={false} />

          {showTooltip && (
            <ChartTooltip
              content={
                <ChartTooltipContent formatter={tooltipFormatter} labelClassName="font-normal text-muted-foreground" />
              }
            />
          )}
          <Bar dataKey="value" fill={barColor} maxBarSize={100} radius={[2, 2, 0, 0]}>
            {showTopBarLabels && (
              <LabelList dataKey="value" fill="var(--foreground)" formatter={topLabelFormatter} position="top" />
            )}
          </Bar>
          {showLegend && <ChartLegend content={<ChartLegendContent nameKey="value" />} verticalAlign="bottom" />}
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

/**
 * Memoized version of ReportBarChart
 *
 * Prevents re-renders when props haven't changed, improving performance.
 */
export const ReportBarChart = memo(ReportBarChartComponent);
